import { createStore, createEffect } from "effector"
import { useUnit } from "effector-react"

import { UserStorageKey } from "modules/UserStorage/constants"
import UserStorageService from "modules/UserStorage/services/UserStorageService"
import { AssetField } from "hooks/useAssetFields"
import { AssetTechnicalDataField } from "hooks/useAssetTechnicalDataFields"

// Stores
const $pinnedFields = createStore<(AssetField | AssetTechnicalDataField)[] | null>(null)

// Effects
const fetchPinnedFields = createEffect(() => {
    return UserStorageService.getValue(UserStorageKey.assetSidebarPinnedFields)
})

const pinField = createEffect(async (field: AssetField | AssetTechnicalDataField) => {
    const pinnedFields = $pinnedFields.getState()
    if (!pinnedFields || pinnedFields.includes(field)) return pinnedFields
    const newPinnedFields = [...pinnedFields, field]
    await UserStorageService.setValue(UserStorageKey.assetSidebarPinnedFields, newPinnedFields)
    return newPinnedFields
})

const unpinField = createEffect(async (field: AssetField | AssetTechnicalDataField) => {
    const pinnedFields = $pinnedFields.getState()
    if (!pinnedFields || !pinnedFields.includes(field)) return pinnedFields
    const newPinnedFields = pinnedFields.filter((f) => f !== field)
    await UserStorageService.setValue(UserStorageKey.assetSidebarPinnedFields, newPinnedFields)
    return newPinnedFields
})

const toggleFieldPin = createEffect(async (field: AssetField | AssetTechnicalDataField) => {
    const pinnedFields = $pinnedFields.getState()
    if (pinnedFields?.includes(field)) return unpinField(field)
    return pinField(field)
})

// Listeners
$pinnedFields.on(fetchPinnedFields.doneData, (state, payload) => {
    if (payload.status === "unreadable") return state
    if (payload.status === "empty") return []
    return payload.value.filter((field): field is AssetField | AssetTechnicalDataField =>
        field in AssetField || field in AssetTechnicalDataField
    )
})

$pinnedFields.on(pinField.doneData, (_, payload) => payload)
$pinnedFields.on(unpinField.doneData, (_, payload) => payload)

// Exports
export function useAssetPinnedFields() {
    return {
        pinnedFields: useUnit($pinnedFields),
        fetchPinnedFields,
        pinField,
        unpinField,
        toggleFieldPin,
    }
}
